import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Layout, Spin } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import AppHeader from './components/Header/Header';
import HomePage from './pages/HomePage/HomePage';
import AuthPage from './pages/AuthPage/AuthPage';
import ServicesPage from './pages/ServicesPage/ServicesPage';

import AnalyticsPage from './pages/AnalyticsPage/AnalyticsPage';
import AboutPage from './pages/AboutPage/AboutPage';
import ContactsPage from './pages/ContactsPage/ContactsPage';
import ProtectedRoute from './components/ProtectedRoute';
import GuestOnlyRoute from './components/GuestOnlyRoute';
import { AuthProvider } from './context/AuthContext';
import { UnsavedChangesProvider, UnsavedHistoryGuard } from './context/UnsavedChangesContext';
import './App.css';
import 'leaflet/dist/leaflet.css';
import SiteFooter from './components/SiteFooter';
import RouteEffects from './components/RouteEffects';
import SiteMotion from './components/SiteMotion';
import LegalPage from './pages/LegalPage/LegalPage';
import './workspace-theme.css';

const { Content } = Layout;
const PersonalAcc = lazy(() => import('./pages/PersonalAcc/PersonalAcc'));

const App = () => {
    const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    useEffect(() => {
        const media = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setReducedMotion(media.matches);
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);
    return (
        <ConfigProvider
            locale={ruRU}
            theme={{
                token: {
                    motion: !reducedMotion,
                    colorPrimary: '#1890ff',
                    borderRadius: 10,
                    fontFamily: "'Onest Variable', 'Segoe UI', sans-serif",
                    colorText: '#262626',
                    colorTextSecondary: '#595959',
                    colorLink: '#096dd9',
                    controlHeight: 42,
                },
                components: {
                    Button: { colorPrimary: '#096dd9', colorPrimaryHover: '#096dd9', colorPrimaryActive: '#096dd9', primaryShadow: 'none' },
                    Tabs: { itemSelectedColor: '#096dd9', itemHoverColor: '#096dd9', inkBarColor: '#1890ff' },
                },
            }}
        >
            <AuthProvider>
                <UnsavedChangesProvider>
                <Router>
                    <RouteEffects />
                    <SiteMotion reducedMotion={reducedMotion} />
                    <UnsavedHistoryGuard />
                    <Layout className="app-layout">
                        <AppHeader />
                        <Content id="main-content" className="app-content" tabIndex={-1}>
                            <Routes>
                                <Route path="/" element={<HomePage />} />
                                <Route path="/home" element={<HomePage />} />

                                <Route
                                    path="/login"
                                    element={
                                        <GuestOnlyRoute>
                                            <AuthPage />
                                        </GuestOnlyRoute>
                                    }
                                />

                                <Route
                                    path="/register"
                                    element={
                                        <GuestOnlyRoute>
                                            <AuthPage />
                                        </GuestOnlyRoute>
                                    }
                                />
                                <Route path="/forgot-password" element={<AuthPage />} />
                                <Route path="/reset-password" element={<AuthPage />} />

                                <Route path="/analytics" element={<AnalyticsPage />} />
                                <Route path="/services" element={<ServicesPage />} />
                                <Route path="/about" element={<AboutPage />} />
                                <Route path="/contacts" element={<ContactsPage />} />
                                <Route path="/privacy" element={<LegalPage type="privacy" />} />
                                <Route path="/consent" element={<LegalPage type="consent" />} />
                                <Route path="/terms" element={<LegalPage type="terms" />} />

                                <Route
                                    path="/personal"
                                    element={
                                        <ProtectedRoute>
                                            <Suspense fallback={<div className="workspace-loading" role="status"><Spin /> Загрузка кабинета…</div>}><PersonalAcc /></Suspense>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </Content>
                        <SiteFooter />
                    </Layout>
                </Router>
                </UnsavedChangesProvider>
            </AuthProvider>
        </ConfigProvider>
    );
};

export default App;
